import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUserAuth, getUserToken } from "../context/UserAuthContext";

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO = "https://customer-assets.emergentagent.com/job_261f6229-25c6-462a-bf7c-d1c15ef074eb/artifacts/rv1gzzra_logo%20v2%20xpro.png";

const QUESTIONS = [
  { key: "problema_cardiaco",        text: "¿Algún profesional sanitario te ha indicado que tienes un problema cardíaco o que debes limitar tu actividad física?" },
  { key: "dolor_pecho_ejercicio",    text: "¿Sientes dolor en el pecho durante el ejercicio?" },
  { key: "mareos_desmayos",          text: "¿Has sufrido mareos, desmayos o pérdida de conocimiento durante o después del ejercicio?" },
  { key: "falta_aire_inusual",       text: "¿Te falta el aire de forma inusual con esfuerzos que otras personas consideran normales?" },
  { key: "otra_condicion_medica",    text: "¿Tienes alguna otra condición médica que debamos conocer?", hasDetail: true, detailKey: "otra_condicion_medica_detalle", detailLabel: "Descríbela brevemente (opcional)" },
  { key: "medicacion_relevante",     text: "¿Tomas algún medicamento relevante para el ejercicio?", hasDetail: true, detailKey: "medicacion_relevante_detalle", detailLabel: "Indica cuál (opcional)" },
  { key: "lesion_reciente_sin_recuperar", text: "¿Tienes una lesión reciente sin recuperar completamente?" },
];

export default function OnboardingSafetyPage() {
  const navigate = useNavigate();
  const { refreshUser } = useUserAuth();

  const [answers, setAnswers] = useState(() =>
    Object.fromEntries(QUESTIONS.map((q) => [q.key, null]))
  );
  const [details, setDetails] = useState({});
  const [consent, setConsent] = useState(false);
  const [firmante, setFirmante] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const allAnswered = QUESTIONS.every((q) => answers[q.key] !== null);
  const canSubmit = allAnswered && consent && firmante.trim().length >= 2;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError("");
    try {
      const body = {
        ...Object.fromEntries(QUESTIONS.map((q) => [q.key, answers[q.key]])),
        otra_condicion_medica_detalle: details.otra_condicion_medica_detalle || null,
        medicacion_relevante_detalle: details.medicacion_relevante_detalle || null,
        consentimiento_datos_salud: true,
        consentimiento_nombre_firmante: firmante.trim(),
      };
      const res = await fetch(`${API}/api/onboarding/seguridad`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getUserToken()}` },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || "Error al guardar"); return; }
      await refreshUser();
      if (data.hay_bandera_roja) {
        navigate("/mi-rutina", { replace: true });
      } else {
        navigate("/onboarding/personalizacion", { replace: true });
      }
    } catch {
      setError("Error de conexión. Inténtalo de nuevo.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-black/95 backdrop-blur border-b border-white/8">
        <div className="max-w-lg mx-auto px-4 h-14 flex items-center justify-between">
          <img src={LOGO} alt="XPRO FITNESS" className="h-7 w-auto" />
          <span className="text-[10px] tracking-[0.25em] uppercase text-white/40">Paso 1 de 2</span>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 py-8 pb-16">
        <div className="mb-8">
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#F2C230] font-black mb-2">Cuestionario de seguridad</p>
          <h1 className="text-3xl font-black leading-tight">Antes de empezar</h1>
          <p className="text-white/50 text-sm mt-2">Responde con sinceridad. Todos los datos son confidenciales.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="safety-form">
          {QUESTIONS.map((q, i) => (
            <div key={q.key} className="border border-white/8 p-5">
              <p className="text-sm font-bold text-white/90 leading-relaxed mb-4">
                <span className="text-[#F2C230] mr-2">{i + 1}.</span>{q.text}
              </p>
              <div className="flex gap-3">
                {[true, false].map((val) => (
                  <button
                    key={String(val)}
                    type="button"
                    data-testid={`${q.key}-${val ? "si" : "no"}`}
                    onClick={() => setAnswers((prev) => ({ ...prev, [q.key]: val }))}
                    className={`flex-1 py-3 font-black text-xs tracking-[0.2em] uppercase transition-colors ${
                      answers[q.key] === val
                        ? val ? "bg-red-500/20 border border-red-500/50 text-red-300" : "bg-green-500/15 border border-green-500/40 text-green-300"
                        : "border border-white/10 text-white/50 hover:border-white/25"
                    }`}
                  >
                    {val ? "Sí" : "No"}
                  </button>
                ))}
              </div>
              {q.hasDetail && answers[q.key] === true && (
                <textarea
                  rows={2}
                  placeholder={q.detailLabel}
                  value={details[q.detailKey] || ""}
                  onChange={(e) => setDetails((p) => ({ ...p, [q.detailKey]: e.target.value }))}
                  className="mt-3 w-full bg-[#111] border border-white/10 text-white/80 placeholder:text-white/25 px-4 py-3 text-sm outline-none focus:border-[#F2C230] resize-none transition-colors"
                />
              )}
            </div>
          ))}

          {/* Legal consent */}
          <div className="border border-[#F2C230]/30 bg-[#F2C230]/5 p-5 space-y-5">
            <h2 className="text-sm font-black tracking-wider uppercase text-[#F2C230]">Consentimiento y Declaración</h2>

            <div className="text-xs text-white/60 leading-relaxed space-y-3">
              <p><strong className="text-white/80">1. Tratamiento de datos de salud (RGPD art. 9)</strong><br />
              Los datos relativos a tu salud tienen la consideración de categoría especial de datos. Al marcar esta casilla, otorgas tu consentimiento explícito para que XPRO FITNESS trate esta información con la única finalidad de valorar la idoneidad del ejercicio físico recomendado y garantizar tu seguridad. Estos datos no se cederán a terceros salvo obligación legal.</p>
              <p><strong className="text-white/80">2. Declaración de responsabilidad</strong><br />
              Declaro que la información proporcionada es veraz y completa a fecha de hoy, y me comprometo a comunicar a XPRO FITNESS cualquier cambio relevante en mi estado de salud. Entiendo que la actividad física conlleva un riesgo inherente y que, si tengo dudas, XPRO FITNESS me recomienda consultar con un profesional médico antes de iniciar cualquier programa.</p>
            </div>

            <label className="flex items-start gap-3 cursor-pointer group" data-testid="consent-checkbox-label">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                data-testid="consent-checkbox"
                className="mt-0.5 w-4 h-4 accent-[#F2C230] cursor-pointer"
              />
              <span className="text-xs text-white/75 leading-relaxed">
                He leído y otorgo mi consentimiento explícito al tratamiento de mis datos de salud según lo descrito arriba. <span className="text-red-400">*</span>
              </span>
            </label>

            <div>
              <label className="block text-[10px] tracking-[0.2em] uppercase font-bold text-white/50 mb-1.5">
                Escribe tu nombre completo como firma <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={firmante}
                onChange={(e) => setFirmante(e.target.value)}
                placeholder="Tu nombre completo"
                data-testid="firmante-input"
                className="w-full bg-black border border-white/15 text-white px-4 py-3 text-sm outline-none focus:border-[#F2C230] transition-colors"
              />
              <p className="text-[10px] text-white/30 mt-1">La fecha se registra automáticamente por el servidor.</p>
            </div>
          </div>

          {error && <p className="text-red-400 text-sm" data-testid="safety-error">{error}</p>}

          <button
            type="submit"
            disabled={!canSubmit || submitting}
            data-testid="safety-submit"
            className="w-full bg-[#F2C230] text-black font-black text-[11px] tracking-[0.25em] uppercase py-4 hover:bg-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {submitting ? "Guardando..." : "Continuar"}
          </button>

          {!canSubmit && allAnswered && (
            <p className="text-white/35 text-xs text-center">
              Debes marcar el consentimiento y escribir tu nombre para continuar.
            </p>
          )}
        </form>
      </div>
    </div>
  );
}
